import { useState } from 'react';
import { Folder, Files } from 'lucide-react';
import type { JobFileEntry, JobFolder } from '@jobos/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useMoveJobFiles } from '@/hooks/useJobFiles';
import { useJobFolders } from '@/hooks/useJobFolders';
import { describeMoveResult } from './jobFileMoveResult';

// Move files into a folder, or back to the job root. One file from the actions
// menu, or a whole selection from the selection bar.
//
// The one failure worth explaining in place is the expense lock: it isn't a
// transient error and choosing a different folder won't help, so with a single
// file the dialog names the reason and stays open rather than toasting
// "failed". A batch has no single reason to give, so it reports what happened
// and closes.

const LOCKED_MESSAGE =
  'This file is attached to an expense. Delete the expense first, then move it.';

interface JobFileMoveDialogProps {
  jobId: string;
  /** The files to move; null or empty closes the dialog. */
  files: JobFileEntry[] | null;
  onClose: () => void;
}

export function JobFileMoveDialog({ jobId, files, onClose }: JobFileMoveDialogProps) {
  const open = files != null && files.length > 0;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {open && (
        <DialogContent className="max-w-sm">
          {/* Keyed on the batch so reopening on a different one starts from that
              batch's folder rather than the last one's. */}
          <MoveForm
            key={files.map((file) => file.id).join(',')}
            jobId={jobId}
            files={files}
            onClose={onClose}
          />
        </DialogContent>
      )}
    </Dialog>
  );
}

function MoveForm({
  jobId,
  files,
  onClose,
}: {
  jobId: string;
  files: JobFileEntry[];
  onClose: () => void;
}) {
  const { data: folders } = useJobFolders(jobId);
  const { mutateAsync: moveFiles, isPending } = useMoveJobFiles(jobId);
  const { toast } = useToast();

  // Where the batch already lives, when it all lives in one place. A mixed
  // batch starts at the root rather than at one arbitrary member's folder.
  const first = files[0]!;
  const sharedFolderId = files.every((file) => file.folderId === first.folderId)
    ? first.folderId
    : null;

  const [target, setTarget] = useState<string | null>(sharedFolderId);
  const [error, setError] = useState<string | null>(null);

  // Names are only unique within a folder now, so a bare name can appear twice.
  // The parent qualifies it — without this, two "Kitchen" rows are identical to
  // the eye and to a screen reader.
  const folderLabel = (folder: JobFolder): string => {
    if (!folder.parentFolderId) return folder.name;
    const parent = (folders ?? []).find((candidate) => candidate.id === folder.parentFolderId);
    return parent ? `${parent.name} / ${folder.name}` : folder.name;
  };

  const options: { id: string | null; name: string }[] = [
    { id: null, name: 'All files' },
    ...(folders ?? []).map((folder) => ({ id: folder.id, name: folderLabel(folder) })),
  ];

  const targetName = options.find((option) => option.id === target)?.name ?? 'the folder';

  const submit = async () => {
    // Already there: nothing to say to the server.
    if (target === sharedFolderId) {
      onClose();
      return;
    }

    setError(null);
    const result = await moveFiles({ ids: files.map((file) => file.id), folderId: target });

    // One file, refused for the one reason worth explaining in place: picking a
    // different folder will not help, so the dialog stays open and says so.
    if (files.length === 1 && result.locked.length === 1) {
      setError(LOCKED_MESSAGE);
      return;
    }

    const message = describeMoveResult(result, targetName);
    if (message) {
      toast({
        title: 'Move files',
        description: message,
        variant: result.moved.length > 0 ? 'default' : 'destructive',
      });
    }
    onClose();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{files.length === 1 ? 'Move file' : `Move ${files.length} files`}</DialogTitle>
        <DialogDescription className="truncate">
          {files.length === 1 ? first.fileName : `${files.length} files selected`}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-1 py-4" role="radiogroup" aria-label="Destination folder">
        {options.map((option) => {
          const selected = option.id === target;
          const Icon = option.id === null ? Files : Folder;
          return (
            <button
              key={option.id ?? 'root'}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={option.name}
              onClick={() => setTarget(option.id)}
              className={`flex min-h-[44px] w-full items-center gap-2.5 rounded border px-3 text-left text-sm ${
                selected
                  ? 'border-blue-500 bg-blue-50 text-blue-800'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
              <span className="truncate">{option.name}</span>
            </button>
          );
        })}
      </div>

      {error && <p className="pb-2 text-sm text-red-600">{error}</p>}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="button" onClick={submit} disabled={isPending}>
          {isPending ? 'Moving…' : 'Move'}
        </Button>
      </DialogFooter>
    </>
  );
}
